/**
 * InboxLayoutPage - WhatsApp-style split view for Inbox
 *
 * Desktop: Threads list on left, thread content on right (via Outlet)
 * Mobile: Single-pane (threads list OR thread content)
 *
 * Routes:
 * - /inbox → threads list (index shows "Select a conversation")
 * - /inbox/@username → thread content
 */

import { Outlet, useParams } from 'react-router-dom';
import { InboxThreadsList } from '../components/InboxThreadsList';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Inbox } from 'lucide-react';
import { useBackNavigation } from '../hooks/useBackNavigation';

export function InboxLayoutPage() {
  const { user } = useAuth();
  const { username } = useParams<{ username: string }>();
  const goBack = useBackNavigation();

  // Whether we're viewing a specific thread
  const hasActiveThread = !!username;

  // Redirect if not logged in
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <button
            onClick={goBack}
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-3"
          >
            <ArrowLeft size={16} />
            <span className="text-sm font-medium">Back</span>
          </button>
          <p className="text-slate-400">Please sign in to view your inbox</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-hidden flex flex-col">
      <div className="flex-shrink-0 px-4 py-3 border-b border-white/[0.06] bg-[#0F1419]">
        <button
          onClick={goBack}
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
          aria-label="Go back"
        >
          <ArrowLeft size={18} />
          <span className="text-sm font-medium">Back</span>
        </button>
      </div>

      {/* Desktop: Split view layout - fixed height, no body scroll */}
      <div className="flex flex-1 min-h-0">
        {/* Left panel: Threads list */}
        {/* On mobile, hide when viewing a thread */}
        <div
          className={`w-full md:w-[360px] lg:w-[400px] flex-shrink-0 border-r border-white/[0.06] overflow-hidden ${
            hasActiveThread ? 'hidden md:flex md:flex-col' : 'flex flex-col'
          }`}
        >
          <InboxThreadsList />
        </div>

        {/* Right panel: Thread content or empty state */}
        {/* On mobile, show full width when viewing a thread */}
        <div
          className={`flex-1 overflow-hidden ${
            hasActiveThread ? 'flex flex-col' : 'hidden md:flex md:flex-col'
          }`}
        >
          {hasActiveThread ? (
            <Outlet />
          ) : (
            <div className="flex-1 flex items-center justify-center bg-[#0A0D10]">
              <div className="text-center">
                <Inbox size={48} className="mx-auto mb-4 text-slate-600" />
                <h2 className="text-lg font-medium text-slate-400 mb-2">
                  Select a conversation
                </h2>
                <p className="text-sm text-slate-500">
                  Choose a thread from the list to view messages
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
